<template lang="pug">
.draft-message-table-component
  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません

    // セル：送信先
    template(#cell(sendTarget)="row")
      .overflow-hidden.send-target {{ getSendTarget(row.item) }}
    // セル：宛先
    template(#cell(targets)="row")
      .overflow-hidden.targets(:id="`targets-${row.item.id}`") {{ getTargets(row.item) }}
      b-popover(v-if="getTargets(row.item) != ''" :target="`targets-${row.item.id}`" triggers="hover" :placement="'bottomright'")
        .d-flex.flex-column
          span {{ getTargets(row.item) }}
    // セル：タイトル
    template(#cell(title)="row")
      .overflow-hidden.title(:id="`title-${row.item.id}`") {{ row.item.title }}
      b-popover(v-if="row.item.title" :target="`title-${row.item.id}`" triggers="hover" :placement="'bottomright'")
        .d-flex.flex-column
          span {{ row.item.title }}
    // セル：登録情報
    template(#cell(operation)="row")
      button.btn.edit-btn(@click="onEdit(row.item)" type="button") 編集
      button.btn.delete-btn(@click="onDelete(row.item)" type="button") 削除
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { DraftMessageFile, DraftMessageSendTargetType } from "@/commons/services/DraftMessages";
import { GroupType, SendMessageGroupsGetModel } from "@/commons/services/SendMessageGroups";
import { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import OtherUserGroup from "@/commons/models/OtherUserGroup";
import { FamilyGetModel } from "@/commons/services/Families";
import { WaiterFamiliesGetModel } from "@/commons/services/WaiterFamilies";

/**
 * ### メッセージ下書きテーブルコンポーネント
 */
@Component({})
export default class DraftMessageTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<DraftMessageTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<DraftMessageTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;

  /** 家族名MAP */
  @Prop()
  familyMap!: Map<string, FamilyGetModel>;
  /** 待機者家族名MAP */
  @Prop()
  waiterFamilyMap!: Map<string, WaiterFamiliesGetModel>;
  /** 施設関係者グループメンバーMAP */
  @Prop()
  otherUserGroupMemberMap!: Map<string, string>;
  /** 宛先グループMAP */
  @Prop()
  sendMessageGroupMap!: Map<string, SendMessageGroupsGetModel>;
  /** 利用者グループMAP */
  @Prop()
  residentGroupMap!: Map<string, ResidentGroupsGetModel>;
  /** 施設関係者グループMAP */
  @Prop()
  otherUserGroupMap!: Map<string, OtherUserGroup>;

  get _value(): Array<DraftMessageTableItem> {
    return this.value;
  }

  /**
   * ### 送信先ラベルを取得する
   */
  public getSendTarget(draftMessage: DraftMessageTableItem): string {
    if (draftMessage.type == undefined) {
      return "";
    }

    let sendTarget: string = "";

    switch (draftMessage.type) {
      case DraftMessageSendTargetType.ALL_FAMILY:
      case DraftMessageSendTargetType.SELECTED_FAMILY: {
        sendTarget = "利用者家族";
        break;
      }
      case DraftMessageSendTargetType.ALL_WAITER:
      case DraftMessageSendTargetType.SELECTED_WAITER: {
        sendTarget = "入所申込者家族";
        break;
      }
      case DraftMessageSendTargetType.SELECTED_OTHER_USER_GROUP: {
        sendTarget = "施設関係者";
        break;
      }
      case DraftMessageSendTargetType.SEND_MESSGE_GROUP: {
        // 下書き保存で宛先が無い場合
        if (draftMessage.targets == undefined || draftMessage.targets.length < 1) {
          sendTarget = "";
        }
        // 下書き保存で宛先がある場合
        else {
          for (const groupSortKey of draftMessage.targets) {
            const sendMessageGroup: SendMessageGroupsGetModel | undefined = this.sendMessageGroupMap.get(groupSortKey);
            const residentGroup: ResidentGroupsGetModel | undefined = this.residentGroupMap.get(groupSortKey);
            const otherUserGroup: OtherUserGroup | undefined = this.otherUserGroupMap.get(groupSortKey);

            // どのグループにも該当しなければ宛先不明
            if (!sendMessageGroup && !residentGroup && !otherUserGroup) {
              sendTarget = "";
            }
            // 宛先グループの場合
            else if (sendMessageGroup) {
              const group: SendMessageGroupsGetModel | undefined = this.sendMessageGroupMap.get(draftMessage.targets[0] as string);

              if (group == undefined) {
                sendTarget = "";
              } else if (group.type == GroupType.FAMILY) {
                sendTarget = "利用者家族";
              } else if (group.type == GroupType.OTHER_USER) {
                sendTarget = "施設関係者";
              }
            }
            // 利用者グループの場合
            else if (residentGroup) {
              sendTarget = "利用者家族";
            }
            // 施設関係者グループの場合
            else if (otherUserGroup) {
              sendTarget = "施設関係者";
            }
          }
        }
        break;
      }
    }

    return sendTarget;
  }

  /**
   * ### 宛先ラベルを取得する
   */
  public getTargets(draftMessage: DraftMessageTableItem): string {
    if (draftMessage.type == undefined) {
      return "";
    }

    if (draftMessage.type == DraftMessageSendTargetType.ALL_FAMILY) {
      return "家族全員";
    }

    if (draftMessage.type == DraftMessageSendTargetType.ALL_WAITER) {
      return "入所申込者全員";
    }

    if (draftMessage.targets == undefined || draftMessage.targets.length < 1) {
      return "";
    }

    const targets: string[] = [];
    switch (draftMessage.type) {
      case DraftMessageSendTargetType.SELECTED_FAMILY: {
        for (const familySortKey of draftMessage.targets) {
          const family: FamilyGetModel | undefined = this.familyMap.get(familySortKey);
          if (family != undefined) {
            targets.push(`${family.familyName}（${family.room}・${family.name}）`);
          } else {
            targets.push("宛先不明");
          }
        }
        break;
      }
      case DraftMessageSendTargetType.SELECTED_WAITER: {
        for (const familySortKey of draftMessage.targets) {
          const family: WaiterFamiliesGetModel | undefined = this.waiterFamilyMap.get(familySortKey);
          if (family != undefined) {
            targets.push(family.familyName);
          } else {
            targets.push("宛先不明");
          }
        }

        break;
      }
      case DraftMessageSendTargetType.SELECTED_OTHER_USER_GROUP: {
        for (const memberSortKey of draftMessage.targets) {
          const name: string | undefined = this.otherUserGroupMemberMap.get(memberSortKey);
          if (name != undefined) {
            targets.push(name);
          } else {
            targets.push("宛先不明");
          }
        }
        break;
      }
      case DraftMessageSendTargetType.SEND_MESSGE_GROUP: {
        for (const groupSortKey of draftMessage.targets) {
          const sendMessageGroup: SendMessageGroupsGetModel | undefined = this.sendMessageGroupMap.get(groupSortKey);
          const residentGroup: ResidentGroupsGetModel | undefined = this.residentGroupMap.get(groupSortKey);
          const otherUserGroup: OtherUserGroup | undefined = this.otherUserGroupMap.get(groupSortKey);

          // どのグループにも該当しなければ宛先不明
          if (!sendMessageGroup && !residentGroup && !otherUserGroup) {
            targets.push("宛先不明");
          }
          // 宛先グループの場合
          else if (sendMessageGroup) {
            targets.push(sendMessageGroup.name);
          }
          // 利用者グループの場合
          else if (residentGroup) {
            targets.push(residentGroup.name);
          }
          // 施設関係者グループの場合
          else if (otherUserGroup) {
            targets.push(otherUserGroup.name);
          }
        }
        break;
      }
    }

    return targets.join("、");
  }

  /**
   * ### 編集ボタン押下イベント
   */
  public onEdit(draftMessage: DraftMessageTableItem): void {
    this.$emit("onEdit", draftMessage);
    return;
  }

  /**
   * ### 削除ボタン押下イベント
   */
  public onDelete(draftMessage: DraftMessageTableItem): void {
    this.$emit("onDelete", draftMessage);
    return;
  }
}

/** テーブルの列名の情報 */
export interface DraftMessageTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface DraftMessageTableItem {
  /** 施設ID(パーティションキー) */
  facilityId: number;
  /** 識別ID(ソートキー) */
  id: number;
  /** 宛先種別 */
  type?: DraftMessageSendTargetType;
  /**
   * 宛先
   *
   * - <家族の場合>
   *   - residentsSortKeyを配列で持つ
   * - <施設関係者の場合>
   *   - otherUserGroupsSortKeyを配列で持つ
   * - <宛先グループの場合>
   *   - sendMessageGroupsSortKeyを配列で持つ
   */
  targets?: string[];
  /** タイトル */
  title?: string;
  /** 本文 */
  content?: string;
  /** メッセージの返信が欲しいことを強調させるか */
  addOptionText?: boolean;
  /**  見ましたをつけるか*/
  addReadButton?: boolean;
  /** 返信をつけるか */
  addReplyButton?: boolean;
  /** 添付ファイル */
  files?: Array<DraftMessageFile>;
  /** 記載者ID */
  writerId: string;
  /** 記載者 */
  writtenBy: string;
  /** 更新日時 */
  updated: number;
  /** 作成日時 */
  created: number;
}
</script>

<style lang="scss" scoped>
.draft-message-table-component {
  // 文字がtd要素の幅をはみ出した場合「…」で隠すスタイル
  .overflow-hidden {
    overflow: hidden !important;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .edit-btn,
  .delete-btn {
    width: 64px;
  }
  .edit-btn {
    margin-right: 5px;
  }
}
</style>
